import { api } from './api-base';

// Types for the saves functionality
export type SaveListVisibility = 'PUBLIC' | 'PRIVATE_SELF' | 'FOLLOWERS' | 'MUTUALS' | 'CUSTOM';

export interface SaveList {
  id: string;
  userId: string;
  name: string;
  description?: string;
  // New visibility-based fields
  visibility?: SaveListVisibility;
  audienceUserIds?: string[];
  // Legacy fields (for backward compatibility)
  isPublic?: boolean;
  sharedWith?: string[];
  // Common fields
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    username?: string;
    email: string;
  };
  _count?: {
    saves: number;
  };
  itemCount?: number;
}

export interface Save {
  id: string;
  userId: string;
  contentType: 'TOPIC' | 'COMMENT';
  contentId: string;
  listId?: string;
  note?: string;
  createdAt: string;
  topic?: {
    id: string;
    title: string;
    content: string;
    createdAt: string;
    user: {
      id: string;
      username?: string;
      email: string;
      isDeleted: boolean;
    };
  };
  comment?: {
    id: string;
    content: string;
    createdAt: string;
    user: {
      id: string;
      username?: string;
      email: string;
      isDeleted: boolean;
    };
    topic: {
      id: string;
      title: string;
    };
  };
  list?: {
    id: string;
    name: string;
  };
}

export interface SaveStatus {
  isSaved: boolean;
  saves: Array<{
    id: string;
    list?: {
      id: string;
      name: string;
    };
    note?: string;
    createdAt: string;
  }>;
}

const isVisibleSave = (save: Save | null | undefined): save is Save => {
  if (!save) return false;
  if (save.contentType === 'TOPIC') {
    const topicAny: any = save.topic;
    if (!topicAny) return false;
    if (topicAny.isDeleted) return false;
    if (topicAny.deletedBy) return false;
    if (topicAny.user?.isDeleted) return false;
    if (typeof topicAny.moderationReason === 'string' && topicAny.moderationReason.trim().length > 0) {
      return false;
    }
    return true;
  }
  if (save.contentType === 'COMMENT') {
    const commentAny: any = save.comment;
    if (!commentAny) return false;
    if (commentAny.user?.isDeleted) return false;
    const trimmed = (commentAny.content || '').trim().toLowerCase();
    if (!trimmed || trimmed === '[deleted]' || trimmed === '[removed]') return false;
    if (commentAny.isDeleted) return false;
    if (commentAny.deletedBy) return false;
    if (typeof commentAny.moderationReason === 'string' && commentAny.moderationReason.trim().length > 0) {
      return false;
    }
    const topicAny: any = commentAny.topic;
    if (!topicAny) return false;
    if (topicAny.isDeleted) return false;
    if (topicAny.deletedBy) return false;
    if (topicAny.user?.isDeleted) return false;
    if (typeof topicAny.moderationReason === 'string' && topicAny.moderationReason.trim().length > 0) {
      return false;
    }
    return true;
  }
  return false;
};

// Saves API
export const savesApi = {
  // Save Lists Management

  // Create a new save list
  createList: async (data: {
    name: string;
    description?: string;
    visibility?: SaveListVisibility;
    audienceUserIds?: string[];
    // Legacy support
    isPublic?: boolean;
    isPinned?: boolean;
    sharedWith?: string[];
  }): Promise<SaveList> => {
    const response = await api.post('/saves/lists', data);
    return response.data;
  },

  // Get user's save lists
  getLists: async (includeShared = false): Promise<{ lists: SaveList[] }> => {
    const response = await api.get('/saves/lists', {
      params: { includeShared: includeShared.toString() }
    });
    return response.data;
  },

  // Get a specific save list with its items
  getList: async (listId: string, sort?: 'newest' | 'oldest' | 'alphabetical'): Promise<{
    list: SaveList;
    saves: Save[];
  }> => {
    const response = await api.get(`/saves/lists/${listId}`, {
      params: sort ? { sort } : {}
    });
    return response.data;
  },

  // Update a save list
  updateList: async (listId: string, data: {
    name?: string;
    description?: string;
    visibility?: SaveListVisibility;
    audienceUserIds?: string[];
    // Legacy support
    isPublic?: boolean;
    isPinned?: boolean;
    sharedWith?: string[];
  }): Promise<SaveList> => {
    const response = await api.put(`/saves/lists/${listId}`, data);
    return response.data;
  },

  // Delete a save list
  deleteList: async (listId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/saves/lists/${listId}`);
    return response.data;
  },

  // Toggle pin status for a save list
  toggleListPin: async (listId: string): Promise<SaveList> => {
    const response = await api.post(`/saves/lists/${listId}/pin`);
    return response.data;
  },

  // Individual Saves Management

  // Save an item (topic or comment)
  saveItem: async (data: {
    contentType: 'TOPIC' | 'COMMENT';
    contentId: string;
    listId?: string;
    note?: string;
  }): Promise<Save> => {
    const response = await api.post('/saves', data);
    return response.data;
  },

  // Get user's saves (without specific list)
  getSaves: async (params?: {
    contentType?: 'TOPIC' | 'COMMENT';
    sort?: 'newest' | 'oldest';
  }): Promise<{ saves: Save[] }> => {
    const response = await api.get('/saves', { params });
    return response.data;
  },

  // Remove a save
  removeSave: async (saveId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/saves/${saveId}`);
    return response.data;
  },

  // Move save to different list
  moveSave: async (saveId: string, listId?: string): Promise<Save> => {
    const response = await api.put(`/saves/${saveId}/move`, { listId });
    return response.data;
  },

  // Check if content is saved
  checkSaveStatus: async (contentType: 'TOPIC' | 'COMMENT', contentId: string): Promise<SaveStatus> => {
    const response = await api.get(`/saves/check/${contentType}/${contentId}`);
    return response.data;
  },

  // Utility functions for handling saves

  // Get all saves for a user (including lists)
  getAllUserSaves: async (sort?: 'newest' | 'oldest'): Promise<{
    unlistedSaves: Save[];
    lists: Array<SaveList & { saves: Save[] }>;
  }> => {
    try {
      const [savesResponse, listsResponse] = await Promise.all([
        savesApi.getSaves({ sort }),
        savesApi.getLists(false)
      ]);

      // Get all lists with their saves
      const listsWithSaves = await Promise.all(
        listsResponse.lists.map(async (list) => {
          const listData = await savesApi.getList(list.id, sort);
          const normalizedSaves = Array.isArray(listData.saves)
            ? listData.saves.filter(isVisibleSave)
            : [];
          const derivedCount = normalizedSaves.length;
          return {
            ...list,
            ...(listData.list || {}),
            saves: normalizedSaves,
            itemCount: derivedCount
          };
        })
      );

      return {
        unlistedSaves: Array.isArray(savesResponse.saves)
          ? savesResponse.saves.filter(isVisibleSave)
          : [],
        lists: listsWithSaves
      };
    } catch (error) {
      console.error('Error fetching all user saves:', error);
      throw error;
    }
  },

  // Check if user can access a list
  canAccessList: (list: SaveList, currentUserId: string): boolean => {
    // Owner always has access
    if (list.userId === currentUserId) return true;

    // Check new visibility format
    if (list.visibility) {
      switch (list.visibility) {
        case 'PUBLIC':
          return true;
        case 'PRIVATE_SELF':
          return false;
        case 'FOLLOWERS':
        case 'MUTUALS':
          // Would need follower data to determine - default to false for now
          return false;
        case 'CUSTOM':
          return list.audienceUserIds?.includes(currentUserId) || false;
        default:
          return false;
      }
    }

    // Fallback to legacy format
    return (list.isPublic || false) ||
           (list.sharedWith?.includes(currentUserId) || false);
  },

  // Get display name for content
  getContentDisplayName: (save: Save): string => {
    if (save.topic) {
      return save.topic.title;
    } else if (save.comment) {
      return `Comment on "${save.comment.topic.title}"`;
    }
    return 'Unknown content';
  },

  // Get content URL for navigation
  getContentUrl: (save: Save): string => {
    if (save.topic) {
      return `/t/${encodeURIComponent(save.topic.id)}`;
    } else if (save.comment) {
      return `/t/${encodeURIComponent(save.comment.topic.id)}?comment=${encodeURIComponent(save.comment.id)}`;
    }
    return '/';
  },

  // Batch operations

  // Save multiple items to a list
  saveMultipleItems: async (items: Array<{
    contentType: 'TOPIC' | 'COMMENT';
    contentId: string;
    note?: string;
  }>, listId?: string): Promise<Save[]> => {
    const saves = await Promise.all(
      items.map(item => savesApi.saveItem({ ...item, listId }))
    );
    return saves;
  },

  // Remove multiple saves
  removeMultipleSaves: async (saveIds: string[]): Promise<void> => {
    await Promise.all(saveIds.map(id => savesApi.removeSave(id)));
  },

  // Move multiple saves to a list
  moveMultipleSaves: async (saveIds: string[], listId?: string): Promise<Save[]> => {
    const saves = await Promise.all(
      saveIds.map(id => savesApi.moveSave(id, listId))
    );
    return saves;
  },

  // Public shared list access

  // Get a public/shared save list (no auth required for public lists)
  getPublicList: async (listId: string, sort?: 'newest' | 'oldest' | 'alphabetical'): Promise<{
    list: SaveList;
    saves: Save[];
  }> => {
    const response = await api.get(`/saves/public/lists/${listId}`, {
      params: sort ? { sort } : {}
    });
    return response.data;
  },

  // Get public save lists for a user (for viewing their profile)
  getUserPublicLists: async (userId: string): Promise<{ lists: SaveList[] }> => {
    const response = await api.get(`/saves/users/${userId}/lists`);
    return response.data;
  },

  // Helper: Convert visibility to legacy format for backend
  visibilityToLegacy: (visibility: SaveListVisibility, audienceUserIds: string[]): { isPublic: boolean; sharedWith: string[] } => {
    switch (visibility) {
      case 'PUBLIC':
        return { isPublic: true, sharedWith: [] };
      case 'CUSTOM':
        return { isPublic: false, sharedWith: audienceUserIds };
      case 'PRIVATE_SELF':
      case 'FOLLOWERS':
      case 'MUTUALS':
      default:
        return { isPublic: false, sharedWith: [] };
    }
  },

  // Helper: Convert legacy format to visibility
  legacyToVisibility: (isPublic: boolean, sharedWith: string[]): { visibility: SaveListVisibility; audienceUserIds: string[] } => {
    if (isPublic) {
      return { visibility: 'PUBLIC', audienceUserIds: [] };
    } else if (sharedWith && sharedWith.length > 0) {
      return { visibility: 'CUSTOM', audienceUserIds: sharedWith };
    } else {
      return { visibility: 'PRIVATE_SELF', audienceUserIds: [] };
    }
  }
};
